import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { TOUCH_TARGET_MIN } from "@/theme/tokens";

/**
 * Grows a control to `TOUCH_TARGET_MIN` on coarse pointers only, so desktop density is untouched.
 * `min*` means it can only ever grow a control — safe to merge onto one that already meets the size.
 *
 * OPT-IN, never a shared base: `min-height` always clamps `height`, so applying this inside a widely
 * used primitive silently breaks the 40px row-alignment invariant `CONTROL_HEIGHT.STANDARD` documents —
 * on every tablet and touch-capable laptop, not just phones. Reach for it at the call site that needs a
 * finger target, or inside a component only its own feature consumes.
 *
 * Typed `SystemStyleObject`, not `SxProps`: the latter may itself be an array and MUI rejects a nested
 * one, and this has to stay usable as `sx={[base, TOUCH_TARGET_SX]}`.
 */
export const TOUCH_TARGET_SX: SystemStyleObject<Theme> = {
	"@media (pointer: coarse)": { minWidth: TOUCH_TARGET_MIN, minHeight: TOUCH_TARGET_MIN },
};

/**
 * Grows only the HIT AREA to `TOUCH_TARGET_MIN` on coarse pointers, with an invisible `::after` centred on the control,
 * and leaves the drawn box alone.
 *
 * `TOUCH_TARGET_SX` grows the element itself, which is invisible on a transparent control (an icon button) but turns a
 * filled one (a tinted pill) into a 44px slab beside its 32px neighbours. Use this one for a filled control. The control
 * must be `position: relative` (SystemButton is) and must not draw its own `::after`. Same opt-in rule as above.
 */
export const TOUCH_TARGET_OVERLAY_SX: SystemStyleObject<Theme> = {
	"@media (pointer: coarse)": {
		"&::after": {
			content: '""',
			position: "absolute",
			top: "50%",
			left: "50%",
			width: `max(100%, ${TOUCH_TARGET_MIN}px)`,
			height: `max(100%, ${TOUCH_TARGET_MIN}px)`,
			transform: "translate(-50%, -50%)",
		},
	},
};

/**
 * The height half of `TOUCH_TARGET_SX`, for controls that share a row's width between them.
 *
 * A row of N equal cells (a 7-day strip, a segmented control) cannot also give every cell a 44px
 * minimum WIDTH: on a 320–390px phone that sum overflows the screen, and only on coarse pointers, so a
 * desktop browser at the same width never shows it. The layout decides the width; this keeps the
 * finger-sized height the width had to give up.
 */
export const TOUCH_TARGET_HEIGHT_SX: SystemStyleObject<Theme> = {
	"@media (pointer: coarse)": { minHeight: TOUCH_TARGET_MIN },
};
